import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { utils } from '@grammarly/badger-ui'
import { Document } from '@contentful/rich-text-types'

import { useShouldTriggerWall } from './use-should-trigger-wall'
import { CheetahResultState } from '../../../lib/services/api/cheetah-client'
import { PageContext } from '../../../contexts/page-context'
import { gnarSpec, gnarClient, EventProps } from '../../../lib/tracking/gnar'
import { transformToObject, transformToString } from '../../../lib/utils/error'
import { LogErrorMetrics, logError } from '../../../lib/tracking/felog'
import { rhfToFormData } from '../../form/rhf-helpers'
import { ParaphrasingPromptType, SideBySideClickState } from './utils'
import { feMetrics } from '@lib/tracking/metrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { useGgoActions, useGgoResult, useGgoState } from '@lib/services/api/cheetah-provider'
import { UrlBasedUsageTracker } from '@lib/utils/tool-usage'
import { useExperimentService, useUserService } from '@lib/hooks/services'
import { trackExperimentInfo } from '@lib/tracking/google-tag-manager/events'

export const useCheetahResultStateTracking = (
  cheetahResultState: CheetahResultState,
  cheetahLoadingTimestamp: React.MutableRefObject<number>,
  output: string,
  pageId: string,
  slug: string,
  cheetahError?: unknown
) => {
  useEffect(() => {
    if (cheetahResultState === 'loading') {
      cheetahLoadingTimestamp.current = Date.now()
      gnarSpec.goToolsGenerateLoading(pageId, slug)
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
        .getCounter(CmsMetrics.ClientMetricsActions.Attempt)
        .setContext({
          toolPage: CmsMetrics.ToolType.GoTemplates,
        })
        .increment()
    } else if (cheetahResultState === 'streaming') {
      const timeElapsed = Date.now() - cheetahLoadingTimestamp.current
      gnarSpec.goToolsGenerateStreaming(pageId, slug, timeElapsed)
    } else if (cheetahResultState === 'done') {
      const timeElapsed = Date.now() - cheetahLoadingTimestamp.current
      gnarSpec.goToolsGenerateSuccess(output.length, pageId, slug, timeElapsed)
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
        .getCounter(CmsMetrics.ClientMetricsActions.Success)
        .setContext({
          toolPage: CmsMetrics.ToolType.GoTemplates,
        })
        .increment()
    } else if (cheetahResultState === 'error') {
      console.error('cheetahError', cheetahError)
      gnarSpec.goToolsGenerateFailure(pageId, slug, transformToString(cheetahError as Error))
      logError('Error generating GO Tool response', LogErrorMetrics.GOToolError, {
        error: transformToObject(cheetahError as Error),
      })
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
        .getCounter(CmsMetrics.ClientMetricsActions.Failure)
        .setContext({
          toolPage: CmsMetrics.ToolType.GoTemplates,
        })
        .increment()
    }
    // We only want to re-run this when cheetahResultState changes.
    // Should be refactored to run on event rather than on state change, but it's tricky.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cheetahResultState, pageId, slug])
}

type FormStepTrackingData = {
  activeStep: number
  totalSteps: number
  inputName: string
  inputType: 'long text' | 'short text' | 'file upload'
}

interface GoTemplatesContextValue {
  methods: ReturnType<typeof useForm<Record<string, string>>>
  output: string
  shouldTriggerWall: boolean
  cheetahResultState: CheetahResultState
  submitButtonText: Document
  loadingButtonText?: Document
  outputLabel: Document
  outputDescription: Document
  internalName: string
  isWallOpen: boolean
  showTrialModal: boolean
  setShowTrialModal: (val: boolean) => void
  onFormChangeCapture: (event: React.ChangeEvent<HTMLFormElement>) => void
  onFormClickCapture: (event: React.MouseEvent<HTMLFormElement>) => void
  updatePastedPerStep: (hasPastedValue: boolean) => void
  trackFormStepComplete: (formStepTrackingData: FormStepTrackingData) => void
  onChange: () => void
  getOnSubmit: (
    formStepTrackingData: FormStepTrackingData,
    isSideBySide: boolean
  ) => SubmitHandler<Record<string, string>>
  isLoading: boolean
  paraphrasingPrompt: ParaphrasingPromptType
  setParaphrasingPrompt: (promptType: ParaphrasingPromptType) => void
  generatedParaphrasingPrompt: ParaphrasingPromptType | null
  sideBySideClicked: SideBySideClickState
  gnarTrackFn: (trackEventProps: EventProps) => Promise<void>
}

const [BaseProvider, useGoTemplates] = utils.createContext<GoTemplatesContextValue>({
  name: 'GoTemplates',
})

export interface GoTemplatesProviderProps {
  llmPrompt: Document
  submitButtonText: Document
  loadingButtonText?: Document
  outputLabel: Document
  outputDescription: Document
  internalName: string
  isWallOpen: boolean
  triggerWall: () => void
  children: React.ReactNode
  usageTracker?: UrlBasedUsageTracker
}

export const GoTemplatesProvider = (props: GoTemplatesProviderProps) => {
  const {
    children,
    llmPrompt,
    submitButtonText,
    loadingButtonText,
    outputLabel,
    outputDescription,
    internalName,
    isWallOpen,
    triggerWall,
    usageTracker,
  } = props
  const { getToolsOptimalUsageLimitAssignment, experimentsWithAssignedTreatment } = useExperimentService()
  const { isAnonymous } = useUserService().currentUser
  const methods = useForm({ mode: 'onTouched' })
  const { pageId, slug } = useContext(PageContext)

  const shouldTriggerWall = useShouldTriggerWall(usageTracker)
  const [showTrialModal, setShowTrialModal] = useState(false)
  const { cheetahResultState, error: cheetahError } = useGgoState()
  const output = useGgoResult()
  const { sendPrompt } = useGgoActions()

  const hasPastedPerStep = useRef(false)
  const cheetahLoadingTimestamp = useRef(-1)

  const [sideBySideClicked, setSideBySideClicked] = useState<SideBySideClickState>('notClicked')

  const [paraphrasingPrompt, setParaphrasingPrompt] = useState<ParaphrasingPromptType>('standard')
  const [generatedParaphrasingPrompt, setGeneratedParaphrasingPrompt] = useState<ParaphrasingPromptType | null>(null)

  const hasInitialChangeRef = useRef(false)

  const gnarTrackFn = useCallback(
    (trackEventProps: EventProps) => {
      return gnarClient.track({ ...trackEventProps, pageId, pageSlug: slug })
    },
    [pageId, slug]
  )

  useCheetahResultStateTracking(cheetahResultState, cheetahLoadingTimestamp, output, pageId, slug, cheetahError)

  const onFormChangeCapture = (event: React.ChangeEvent<HTMLFormElement>) => {
    if (shouldTriggerWall) {
      triggerWall()
      event.stopPropagation()
    }
  }

  const onFormClickCapture = (event: React.MouseEvent<HTMLFormElement>) => {
    if (shouldTriggerWall) {
      const formInputTagNames = new Set(['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT'])
      const { target } = event
      const isTargetFormInput =
        formInputTagNames.has((target as HTMLElement).tagName) ||
        formInputTagNames.has((target as HTMLElement).parentElement?.tagName || '') ||
        formInputTagNames.has((target as HTMLElement).parentElement?.parentElement?.tagName || '')
      if (isTargetFormInput) {
        triggerWall()
        event.stopPropagation()
      }
    }
  }

  const updatePastedPerStep = (hasPastedValue: boolean) => {
    hasPastedPerStep.current = hasPastedValue
  }

  const trackFormStepComplete = (formStepTrackingData: FormStepTrackingData) => {
    const { activeStep, totalSteps, inputName, inputType } = formStepTrackingData
    const inputMethod = hasPastedPerStep.current ? 'paste' : 'type'

    let inputValueCharacterCount = undefined

    try {
      const inputValue = methods.getValues<string>(inputName)
      inputValueCharacterCount = inputValue?.length
    } finally {
      gnarSpec.goToolsFormStepComplete(
        activeStep,
        inputName,
        inputType,
        pageId,
        slug,
        totalSteps,
        inputValueCharacterCount,
        inputMethod
      )
    }
  }

  const onChange = () => {
    if (!hasInitialChangeRef.current) {
      getToolsOptimalUsageLimitAssignment(isAnonymous, slug)
      trackExperimentInfo(slug, experimentsWithAssignedTreatment)
      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
        .getCounter(CmsMetrics.ClientMetricsActions.Input)
        .setContext({
          toolPage: CmsMetrics.ToolType.GoTemplates,
        })
        .increment()
      gnarSpec.goToolsFormInputInteraction(pageId, slug)
      hasInitialChangeRef.current = true
    }
  }

  const getOnSubmit: (
    formStepTrackingData: FormStepTrackingData,
    isSideBySide: boolean
  ) => SubmitHandler<Record<string, string>> = (formStepTrackingData, isSideBySide) => {
    return async (data, e) => {
      e?.preventDefault()

      if (shouldTriggerWall) {
        triggerWall()
        return
      }

      if (isSideBySide) {
        setSideBySideClicked('clicked')
      }

      const formData = rhfToFormData(data)
      trackFormStepComplete(formStepTrackingData)
      gnarSpec.goToolsFormSubmit(pageId, slug)

      feMetrics
        .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
        .getCounter(CmsMetrics.ClientMetricsActions.Submit)
        .setContext({
          toolPage: CmsMetrics.ToolType.GoTemplates,
        })
        .increment()

      void sendPrompt(llmPrompt, formData)
    }
  }

  useEffect(() => {
    if (cheetahResultState === 'done') {
      setGeneratedParaphrasingPrompt(paraphrasingPrompt)
    }
  }, [cheetahResultState, setGeneratedParaphrasingPrompt, paraphrasingPrompt])

  const isLoading = cheetahResultState === 'loading' || cheetahResultState === 'streaming'

  return (
    <BaseProvider
      value={{
        methods,
        output,
        shouldTriggerWall,
        cheetahResultState,
        submitButtonText,
        loadingButtonText,
        outputLabel,
        outputDescription,
        internalName,
        isWallOpen,
        showTrialModal,
        setShowTrialModal,
        onFormChangeCapture,
        onFormClickCapture,
        updatePastedPerStep,
        trackFormStepComplete,
        getOnSubmit,
        onChange,
        isLoading,
        paraphrasingPrompt,
        setParaphrasingPrompt,
        generatedParaphrasingPrompt,
        sideBySideClicked,
        gnarTrackFn,
      }}
    >
      {children}
    </BaseProvider>
  )
}

export { useGoTemplates }
